import Node from './nodes/Node';
import ParenthesesOpenToken from './tokens/ParenthesesOpenToken';
import ParenthesesCloseToken from './tokens/ParenthesesCloseToken';
import ExponentiationToken from './tokens/ExponentiationToken';
import PlusToken from './tokens/PlusToken';
import MinusToken from './tokens/MinusToken';
import ProductToken from './tokens/ProductToken';
import DivisionToken from './tokens/DivisionToken';
import NumberToken from './tokens/NumberToken';
import Token from './tokens/Token';
import IndexScanner from './IndexScanner';
import UnaryMinusNode from './nodes/UnaryMinusNode';
import PlusNode from './nodes/PlusNode';
import BinaryMinusNode from './nodes/BinaryMinusNode';
import ProductNode from './nodes/ProductNode';
import DivisionNode from './nodes/DivisionNode';
import ExponentiationNode from './nodes/ExponentiationNode';
import ErrorNode from './nodes/ErrorNode';
import NumberNode from './nodes/NumberNode';
import SetOpenToken from './tokens/SetOpenToken';
import SetCloseToken from './tokens/SetCloseToken';
import SetNode from './nodes/SetNode';
import CommaToken from './tokens/CommaToken';
import SymbolToken from './tokens/SymbolToken';
import SymbolNode from './nodes/SymbolNode';
import VectorOpenToken from './tokens/VectorOpenToken';
import VectorCloseToken from './tokens/VectorCloseToken';
import VectorNode from './nodes/VectorNode';
import SpaceToken from './tokens/SpaceToken';
import SeparatorToken from './tokens/SeparatorToken';
import OperatorToken from './tokens/OperatorToken';
import PhysicalQuantityNode from './nodes/PhysicalQuantityNode';
import AnalysisMode from './AnalysisMode';
import TextNode from './nodes/TextNode';

export default class SyntacticAnalyzer {

	public static execute (tokens: Token[], analysisMode: AnalysisMode): Node {
		// Remove redundant spaces
		tokens = tokens.filter((token, index, tokens) => !(token instanceof SpaceToken) || !(tokens[index - 1] instanceof SpaceToken || tokens[index - 1] instanceof OperatorToken || tokens[index - 1] instanceof SeparatorToken || tokens[index + 1] instanceof OperatorToken || tokens[index + 1] instanceof SeparatorToken));

		const plusMinusIndex = IndexScanner.scanBackward(tokens, token => token instanceof PlusToken || token instanceof MinusToken);

		if (analysisMode === AnalysisMode.MATH && plusMinusIndex !== null) {
			const token = tokens[plusMinusIndex];

			if (plusMinusIndex === 0 && token instanceof MinusToken) {
				return new UnaryMinusNode(this.execute(tokens.slice(1), analysisMode));
			}

			if (plusMinusIndex !== -1) {
				const leftNode = this.execute(tokens.slice(0, plusMinusIndex), analysisMode);
				const rightNode = this.execute(tokens.slice(plusMinusIndex + 1), analysisMode);

				return token instanceof PlusToken ? new PlusNode(leftNode, rightNode) : new BinaryMinusNode(leftNode, rightNode);
			}
		}

		const spaceIndex = IndexScanner.scanBackward(tokens, token => token instanceof SpaceToken);

		if (analysisMode === AnalysisMode.MATH && spaceIndex !== null) {
			const leftNode = this.execute(tokens.slice(0, spaceIndex), analysisMode);
			const rightNode = this.execute(tokens.slice(spaceIndex + 1), AnalysisMode.TEXT);

			return new PhysicalQuantityNode(leftNode, rightNode);
		}

		const productDivisionIndex = IndexScanner.scanBackward(tokens, (token, index, tokens) => token instanceof ProductToken || token instanceof DivisionToken || analysisMode === AnalysisMode.MATH && token instanceof SymbolToken && (tokens[index - 1] instanceof SymbolToken || tokens[index - 1] instanceof NumberToken));

		if (productDivisionIndex !== null) {
			const leftNode = this.execute(tokens.slice(0, productDivisionIndex), analysisMode);
			const rightNode = this.execute(tokens.slice(productDivisionIndex + (tokens[productDivisionIndex] instanceof SymbolToken ? 0 : 1)), analysisMode);

			return tokens[productDivisionIndex] instanceof DivisionToken ? new DivisionNode(leftNode, rightNode) : new ProductNode(leftNode, rightNode);
		}

		const exponentiationIndex = IndexScanner.scanBackward(tokens, token => token instanceof ExponentiationToken);

		if (exponentiationIndex !== null) {
			const leftNode = this.execute(tokens.slice(0, exponentiationIndex), analysisMode);
			const rightNode = this.execute(tokens.slice(exponentiationIndex + 1), analysisMode);

			return new ExponentiationNode(leftNode, rightNode);
		}

		if (tokens[0] instanceof ParenthesesOpenToken && tokens[tokens.length - 1] instanceof ParenthesesCloseToken) {
			return this.execute(tokens.slice(1, tokens.length - 1), analysisMode);
		}

		if (tokens[0] instanceof SetOpenToken && tokens[tokens.length - 1] instanceof SetCloseToken) {
			return new SetNode(this.parseCommaSeparatedTokens(tokens.slice(1, tokens.length - 1), analysisMode));
		}

		if (tokens[0] instanceof VectorOpenToken && tokens[tokens.length - 1] instanceof VectorCloseToken) {
			return new VectorNode(this.parseCommaSeparatedTokens(tokens.slice(1, tokens.length - 1), analysisMode));
		}

		if (analysisMode === AnalysisMode.TEXT && tokens.every(token => token instanceof SymbolToken || token instanceof SpaceToken || token instanceof NumberToken || token instanceof MinusToken || token instanceof CommaToken)) {
			tokens = [tokens.reduce((value: SymbolToken, token: Token) => new SymbolToken(value.getValue() + (token instanceof SymbolToken || token instanceof NumberToken ? token.getValue() : token instanceof MinusToken ? '-' : token instanceof CommaToken ? ',' : ' ')), new SymbolToken(''))];
		}

		if (tokens.length !== 1) {
			return new ErrorNode('Invalid token sequence');
		}

		const firstToken = tokens[0];

		if (firstToken instanceof NumberToken) {
			return new NumberNode(firstToken.getValue());
		}

		if (firstToken instanceof SymbolToken) {
			return analysisMode === AnalysisMode.MATH ? new SymbolNode(firstToken.getValue()) : new TextNode(firstToken.getValue());
		}

		return new ErrorNode('Token expected');
	}

	private static parseCommaSeparatedTokens(tokens: Token[], analysisMode: AnalysisMode) {
		const nodes: Node[] = [];
		let currentSequence: Token[] = [];
		let depth = 0;

		for (let i = 0; i < tokens.length; i++) {
			const token = tokens[i];

			if (depth === 0 && token instanceof CommaToken) {
				nodes.push(this.execute(currentSequence, analysisMode));
				currentSequence = [];
				continue;
			}

			if (token instanceof ParenthesesOpenToken || token instanceof SetOpenToken || token instanceof VectorOpenToken) {
				depth++;
			} else if (token instanceof ParenthesesCloseToken || token instanceof SetCloseToken || token instanceof VectorCloseToken) {
				depth--;
			}

			currentSequence.push(token);
		}

		if (currentSequence.length > 0) {
			nodes.push(this.execute(currentSequence, analysisMode));
		}

		return nodes;
	}
}


// WEBPACK FOOTER //
// ./code/project/modules/parsing/SyntacticAnalyzer.ts